import React, { useCallback, useEffect, useMemo } from 'react';
import { InteractionManager, SectionList, View, } from 'react-native';
import { logAnalyticsEvent } from 'utils/packages/rnFirebase';
import { ShareDialog } from 'utils/packages/fbsdk';
import { muteAll, useFetchOld } from 'hooks';
import { getUser } from 'actions';
import { AppView, Indicator, SCROLL_INDICATOR_INSETS, Spacer, rem, withAnimatedNavigationHeader, } from 'design-system';
import Modal from 'utils/packages/Modal';
import StatusBar from 'design-system/StatusBar';
import FlatListItemCard from 'design-system/FlatListItemCard';
import RateModal from 'features/quests/components/RateModal';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useModalRating from 'features/review/useModalRating';
import Animated from 'react-native-reanimated';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import Sentry from 'utils/sentry';
import { splitIntoRows } from 'utils/arrayHelpers';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import { getQuests, logQuest, setQuestViewed } from '../actions';
import Amplitude from '../../../utils/amplitude';
import QuestItem from '../components/QuestItem';
import QuestModalContent from '../components/QuestModalContent';
import QuestsHeader from '../components/QuestsHeader';
import QuestsSectionHeader from '../components/QuestsSectionHeader';
import { selectQuestsSections, selectQuestsSummary } from '../store/selectors';
import { isWeb } from '../../../constants';
const AnimatedSectionList = Animated.createAnimatedComponent(SectionList);
const keyExtractor = (item) => item[0].type;
const renderSectionHeader = ({ section, }) => {
    if (section && section.data && !section.data.length)
        return null;
    return <QuestsSectionHeader section={section}/>;
};
const QuestsScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const quests = useAppSelector((store) => store.quests.quests);
    const { percentage } = useAppSelector(selectQuestsSummary);
    const questsSections = useAppSelector(selectQuestsSections);
    const source = route.params?.source;
    const styles = useStyles(styleSet);
    useEffect(() => {
        InteractionManager.runAfterInteractions(() => {
            dispatch(getQuests());
        });
        Amplitude.logEvent('Quests.Opened', source ? { source } : undefined);
        logAnalyticsEvent('Open_Quests');
    }, [source]);
    const store = useAppStore();
    const onPressFacebook = useCallback(() => {
        const { userUrl } = store.getState().user.user;
        if (!userUrl)
            return;
        const shareLinkContent = {
            contentType: 'link',
            contentUrl: userUrl,
            contentDescription: 'Check out this free app —— it Pays to Save Money 💰',
        };
        ShareDialog.canShow(shareLinkContent)
            .then((canShow) => {
            if (canShow) {
                return ShareDialog.show(shareLinkContent);
            }
            return undefined;
        })
            .then((result) => {
            if (!result?.isCancelled) {
                fetch(logQuest('FACEBOOK_SHARE')).then((response) => {
                    if (response) {
                        dispatch(getQuests());
                        dispatch(getUser());
                    }
                });
            }
        }, (e) => {
            Sentry.captureException(e);
        });
    }, []);
    const onPressInvite = useCallback((type) => {
        navigation.navigateDeprecated('InviteScreen');
        Amplitude.logEvent('Quests.OpenInvite', { type });
    }, []);
    const [, , , fetch] = useFetchOld(muteAll);
    const dispatch = useAppDispatch();
    const { requestReview } = useModalRating();
    const onPressSubmit = useCallback((data) => {
        Modal.hide(() => {
            if (data > 4) {
                requestReview();
            }
        });
    }, [requestReview]);
    const onQuestPressed = useCallback((data) => {
        if (data &&
            data.status === 'LOCKED' &&
            data.type === 'social.rate_emma') {
            Modal.show(<RateModal onSubmit={onPressSubmit}/>, {
                title: 'Rate this app',
            });
            return;
        }
        Modal.show(<QuestModalContent onPressInvite={() => {
                Modal.hide(() => {
                    onPressInvite(data.type);
                });
            }} onPressShareFacebook={() => {
                Modal.hide(() => {
                    onPressFacebook();
                });
            }} quest={data}/>);
        if (data.status === 'UNLOCKED') {
            InteractionManager.runAfterInteractions(() => {
                fetch(setQuestViewed(data.type)).then((response) => {
                    if (response) {
                        dispatch(getQuests());
                        dispatch(getUser());
                    }
                });
            });
        }
    }, [onPressFacebook, onPressInvite, onPressSubmit]);
    const renderItem = useCallback(({ item, index, section }) => (<FlatListItemCard index={index} style={[
            styles.card,
            index === 0 && styles.firstItem,
            index === section.data.length - 1 && styles.lastItem,
        ]} itemsLength={section.data.length}>
          <View style={styles.row}>
            {item.map((q) => (<QuestItem item={q} key={q.type} onQuestPressed={onQuestPressed}/>))}
          </View>
        </FlatListItemCard>), [
        onQuestPressed,
        styles.card,
        styles.firstItem,
        styles.lastItem,
        styles.row,
    ]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    const renderHeader = useMemo(() => <QuestsHeader percentage={percentage}/>, [percentage]);
    const sectionsIntoRows = useMemo(() => questsSections.map((q) => ({
        ...q,
        data: splitIntoRows(q.data, 3),
    })), [questsSections]);
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content"/>
        {quests.length ? (<AnimatedSectionList sections={sectionsIntoRows} renderItem={renderItem} contentContainerStyle={paddingBottom} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} onScroll={scrollHandler} stickySectionHeadersEnabled={false} ListHeaderComponent={renderHeader} initialNumToRender={25} renderSectionHeader={renderSectionHeader} keyExtractor={keyExtractor}/>) : (<Indicator dark/>)}
        {isWeb ? <Spacer h={24}/> : null}
      </AppView>);
}, ({ colors }) => ({
    title: 'Quests',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    row: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'flex-start',
    },
    card: {
        paddingHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
    },
    firstItem: {
        paddingTop: rem(8),
    },
    lastItem: {
        paddingBottom: rem(8),
    },
}));
export default QuestsScreen;
